import { GuideTask } from '../models/guide.model';

/**
 * Static action registry (MVP).
 *
 * In a later pass this is served from `GET /guide/tasks` so tasks can be added
 * without a redeploy. Kept as a type-checked TS constant for now.
 *
 * Selectors target `data-guide-id` attributes on the real UI. The brief-select
 * flows keep the same URL throughout and swap views on signals, so steps whose
 * target only mounts after a prior action use `waitForSelector`.
 *
 * PRINCIPLE: walkthroughs launched from the AI chat sidebar always favour the
 * **AI-based route** where a flow offers one (e.g. brief creation demonstrates
 * the AI-First "Draft my brief" path, not the Guided Wizard).
 */
export const GUIDE_TASKS: GuideTask[] = [
  {
    id: 'create-screening-brief',
    title: 'Create a new screening brief',
    triggerPhrases: [
      'how do i create a screening brief',
      'create a screening brief',
      'new screening brief',
      'make a screening brief',
      'start a screening brief',
      'screen previous content',
      'set up a screening brief',
    ],
    steps: [
      {
        id: 'goto-briefs',
        action: 'click',
        selector: '[data-guide-id="nav.briefs"]',
        waitForSelector: '[data-guide-id="briefs.addNew"]',
        narration: "First, head to the Briefs tab.",
        requiresUserConfirmation: false,
      },
      {
        id: 'add-new',
        action: 'click',
        selector: '[data-guide-id="briefs.addNew"]',
        waitForSelector: '[data-guide-id="briefs.option.screen-previous-content-v2"]',
        narration: "Click 'Add New' to start a new brief.",
        requiresUserConfirmation: false,
      },
      {
        id: 'choose-type',
        action: 'click',
        selector: '[data-guide-id="briefs.option.screen-previous-content-v2"]',
        waitForSelector: '[data-guide-id="screening.chooseAi"]',
        narration: "Pick 'Screen Previous Content' to screen existing creator content.",
        requiresUserConfirmation: false,
      },
      {
        id: 'choose-ai',
        action: 'click',
        selector: '[data-guide-id="screening.chooseAi"]',
        waitForSelector: '[data-guide-id="screening.aiPrompt"]',
        narration: "We'll let AI draft this for you — I'll open the AI assistant.",
        requiresUserConfirmation: false,
      },
      {
        id: 'type-prompt',
        action: 'type',
        selector: '[data-guide-id="screening.aiPrompt"]',
        inputValue:
          'Find creators who have authentically posted about our product in the last year, with no paid competitor promotions.',
        narration:
          "Describe who you want to screen for in plain English — here's an example you can tweak.",
        requiresUserConfirmation: false,
      },
      {
        id: 'draft',
        action: 'click',
        selector: '[data-guide-id="screening.draft"]',
        narration:
          "When you're happy with the description, click 'Draft my brief' — the AI will build it, then you can review and launch.",
        requiresUserConfirmation: true,
      },
    ],
    isFeatured: true
  },
  {
    id: 'create-campaign-brief',
    title: 'Create a new content review brief',
    triggerPhrases: [
      'how do i create a brief',
      'create a campaign brief',
      'create a brief',
      'post a brief',
      'new brief',
      'start a brief',
      'create a content review brief',
    ],
    steps: [
      {
        id: 'goto-briefs',
        action: 'click',
        selector: '[data-guide-id="nav.briefs"]',
        waitForSelector: '[data-guide-id="briefs.addNew"]',
        narration: "First, head to the Briefs tab.",
        requiresUserConfirmation: false,
      },
      {
        id: 'add-new',
        action: 'click',
        selector: '[data-guide-id="briefs.addNew"]',
        waitForSelector: '[data-guide-id="briefs.option.content-review"]',
        narration: "Click 'Add New' to start a new brief.",
        requiresUserConfirmation: false,
      },
      {
        id: 'choose-type',
        action: 'click',
        selector: '[data-guide-id="briefs.option.content-review"]',
        waitForSelector: '[data-guide-id="createBrief.chooseAi"]',
        narration: "Pick 'Content Review' to create a standard campaign brief.",
        requiresUserConfirmation: false,
      },
      {
        id: 'choose-ai',
        action: 'click',
        selector: '[data-guide-id="createBrief.chooseAi"]',
        waitForSelector: '[data-guide-id="createBrief.aiPrompt"]',
        narration: "We'll let AI draft this for you — I'll open the AI assistant.",
        requiresUserConfirmation: false,
      },
      {
        id: 'select-product',
        action: 'observe',
        selector: '[data-guide-id="createBrief.productSection"]',
        narration:
          "First, choose which product this campaign is about — pick one from 'Which product?'.",
        requiresUserConfirmation: true,
      },
      {
        id: 'type-prompt',
        action: 'type',
        selector: '[data-guide-id="createBrief.aiPrompt"]',
        inputValue:
          "We're launching a new product and want relatable creators showing how it fits into their day.",
        narration:
          "Describe your campaign in plain English — here's an example you can tweak.",
        requiresUserConfirmation: false,
      },
      {
        id: 'draft',
        action: 'click',
        selector: '[data-guide-id="createBrief.draft"]',
        narration:
          "When you're happy with the description, click 'Draft my brief' — the AI will build it, then you can review and launch.",
        requiresUserConfirmation: true,
      },
    ],
    isFeatured: true
  },
  {
    id: 'create-monitoring-brief',
    title: 'Create a new monitoring brief',
    triggerPhrases: [
      'how do i monitor content',
      'create a monitoring brief',
      'monitor new content',
      'set up monitoring',
      'track brand mentions',
      'monitor my brand',
    ],
    steps: [
      {
        id: 'goto-briefs',
        action: 'click',
        selector: '[data-guide-id="nav.briefs"]',
        waitForSelector: '[data-guide-id="briefs.addNew"]',
        narration: "First, head to the Briefs tab.",
        requiresUserConfirmation: false,
      },
      {
        id: 'add-new',
        action: 'click',
        selector: '[data-guide-id="briefs.addNew"]',
        waitForSelector: '[data-guide-id="briefs.option.monitor-new-content"]',
        narration: "Click 'Add New' to start a new brief.",
        requiresUserConfirmation: false,
      },
      {
        id: 'choose-type',
        action: 'click',
        selector: '[data-guide-id="briefs.option.monitor-new-content"]',
        waitForSelector: '[data-guide-id="monitoring.chooseWizard"]',
        narration: "Pick 'Monitor New Content' to track new posts as they go live.",
        requiresUserConfirmation: false,
      },
      {
        id: 'choose-wizard',
        action: 'click',
        selector: '[data-guide-id="monitoring.chooseWizard"]',
        waitForSelector: '[data-guide-id="monitoring.continue"]',
        narration: "Open the guided wizard. You can switch to AI later if you prefer.",
        requiresUserConfirmation: false,
      },
      {
        id: 'continue-to-define',
        action: 'click',
        selector: '[data-guide-id="monitoring.continue"]',
        waitForSelector: '[data-guide-id="monitoring.briefTitle"]',
        narration: "Pick a product if you want one, then I'll move on to the brief name.",
        requiresUserConfirmation: false,
      },
      {
        id: 'type-title',
        action: 'type',
        selector: '[data-guide-id="monitoring.briefTitle"]',
        inputValue: 'Competitor mentions monitor',
        narration:
          "Give your monitoring brief a title, and choose what kind of watch this is. I'll type an example — feel free to change it.",
        requiresUserConfirmation: false,
      },
      {
        id: 'continue-requirements',
        action: 'click',
        selector: '[data-guide-id="monitoring.continue"]',
        narration: "Now on to the requirements — I'll click Continue.",
        requiresUserConfirmation: false,
      },
      {
        id: 'fill-requirements',
        action: 'click',
        selector: '[data-guide-id="monitoring.continue"]',
        narration:
          'Upload a document or write the requirements and any compliance docs, then continue.',
        requiresUserConfirmation: true,
      },
      {
        id: 'set-window',
        action: 'click',
        selector: '[data-guide-id="monitoring.continue"]',
        narration: 'Set the monitoring window, or leave the dates blank to use the default, then continue.',
        requiresUserConfirmation: true,
      },
      {
        id: 'review',
        action: 'click',
        selector: '[data-guide-id="monitoring.continue"]',
        narration: 'Check the summary, then continue to choose who to monitor.',
        requiresUserConfirmation: true,
      },
      {
        id: 'post',
        action: 'click',
        selector: '[data-guide-id="monitoring.continue"]',
        narration: 'Choose who to monitor, then click Submit brief to create your monitoring brief.',
        requiresUserConfirmation: true,
      },
    ],
    isFeatured: true
  },
  {
    id: 'wallet-top-up',
    title: 'Top up your wallet',
    triggerPhrases: [
      'how do i add funds',
      'top up my wallet',
      'add money to my wallet',
      'top up',
      'add credit',
      'add funds to my wallet',
    ],
    steps: [
      {
        id: 'open-wallet-menu',
        action: 'click',
        selector: '[data-guide-id="wallet.balanceButton"]',
        waitForSelector: '[data-guide-id="wallet.addFunds"]',
        narration: "Let's add funds. First, open your wallet menu from the top bar.",
        requiresUserConfirmation: false,
      },
      {
        id: 'add-funds',
        action: 'click',
        selector: '[data-guide-id="wallet.addFunds"]',
        waitForSelector: '[data-guide-id="wallet.recommendedTopUp"]',
        narration: "Choose 'Add Funds' to open the top-up panel.",
        requiresUserConfirmation: false,
      },
      {
        id: 'select-recommended-topup',
        action: 'click',
        selector: '[data-guide-id="wallet.recommendedTopUp"]',
        narration:
          "For simplicity, pick one of the top-up options — this would be perfect starting value. Selecting it takes you to our secure checkout to complete payment.",
        requiresUserConfirmation: true,
      },
    ],
    isFeatured: true
  },
  {
    id: 'find-existing-briefs',
    title: 'Find your existing briefs',
    triggerPhrases: [
      'where do i find my existing briefs',
      'where are my screening briefs',
      'where are my monitoring briefs',
      'where do i find my briefs',
      'find my briefs',
      'see my existing briefs',
      'view my briefs',
      'where are my briefs',
    ],
    steps: [
      {
        id: 'goto-briefs',
        action: 'click',
        selector: '[data-guide-id="nav.briefs"]',
        waitForSelector: '[data-guide-id="briefs.typeFilter"]',
        narration: "Your briefs live under the Briefs tab — I'll take you there.",
        requiresUserConfirmation: false,
      },
      {
        id: 'choose-brief-type',
        action: 'observe',
        selector: '[data-guide-id="briefs.typeFilter"]',
        narration:
          "Use this 'Brief Type' dropdown to switch between your screening and monitoring briefs — pick the type you want to see.",
        requiresUserConfirmation: true,
      },
    ],
    isFeatured: true
  },
];
